import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Settings, X, CheckCircle2, Search } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toolBtnClass } from "@/components/map/MapToolbar";

const same = (l, r) => l.district === r.district && l.tehsil === r.tehsil && l.ri === r.ri && l.village === r.village;
const EMPTY = { district: "", tehsil: "", ri: "", village: "" };

// Ported from the Bolt "FileMapping": gear button on the map (superadmin) → modal listing every village grouped by
// District › Tehsil › R.I., each with a file select. Choosing a file links it to that village instantly.
export function FileMappingControl({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [layers, setLayers] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);

  const load = () => Promise.all([api.get("/admin/locations"), api.get("/admin/layers")]).then(([a, b]) => { setLocations(a.data); setLayers(b.data); }).catch((err) => toast.error(errorMessage(err)));
  useEffect(() => { if (open) load(); }, [open]);

  const groups = useMemo(() => {
    const g = {};
    locations.filter((r) => r.village && (!q || `${r.village} ${r.ri} ${r.tehsil} ${r.district}`.toLowerCase().includes(q.toLowerCase()))).forEach((r) => {
      const key = [r.district, r.tehsil, r.ri].filter(Boolean).join(" › ");
      (g[key] ||= []).push(r);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [locations, q]);

  const fileFor = (row) => layers.find((l) => same(l, row));
  const change = async (row, layerId) => {
    setSaving(row.village);
    try {
      const current = fileFor(row);
      if (current && current.id !== layerId) await api.patch(`/admin/layers/${current.id}`, EMPTY);
      if (layerId) await api.patch(`/admin/layers/${layerId}`, { district: row.district, tehsil: row.tehsil, ri: row.ri, village: row.village });
      await load(); onChanged?.();
      toast.success(layerId ? `Mapping for ${row.village} saved` : `${row.village} unmapped`, { icon: <CheckCircle2 size={16} className="text-emerald-600" /> });
    } catch (err) { toast.error(`Failed to save mapping for ${row.village}: ${errorMessage(err)}`); } finally { setSaving(null); }
  };
  const mapped = layers.filter((l) => l.village).length;

  return (
    <>
      <button type="button" data-testid="file-mapping-control" title="File mapping settings" aria-label="File mapping settings" onClick={() => setOpen(true)} className={toolBtnClass(open)} style={open ? { background: "var(--terracotta)" } : {}}><Settings size={17} /></button>
      {open && createPortal(
        <div className="fixed inset-0 z-[2500] bg-black/50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="file-mapping-dialog" className="w-full max-w-4xl max-h-[80vh] rounded-2xl bg-card border shadow-2xl flex flex-col fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center gap-3">
              <div className="flex-1"><h2 className="text-lg font-semibold">Village file mappings</h2><p className="text-xs text-muted-foreground" data-testid="file-mapping-summary">{mapped} of {layers.length} files mapped · {locations.filter((r) => r.village).length} villages</p></div>
              <div className="relative"><Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><input data-testid="file-mapping-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find village…" className="h-9 pl-8 pr-3 rounded-full border bg-background text-sm w-48" /></div>
              <button type="button" data-testid="file-mapping-close" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-muted" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              {groups.length === 0 && <p className="text-sm text-muted-foreground italic">{locations.length ? "No village matches." : "No villages yet — add them under Admin → Locations."}</p>}
              {groups.map(([key, rows]) => (
                <div key={key} className="border rounded-xl p-4" data-testid="file-mapping-group">
                  <h3 className="font-medium mb-3 text-sm">{key}</h3>
                  <div className="space-y-2">
                    {rows.map((row) => {
                      const cur = fileFor(row);
                      return (
                        <div key={row.id || row.village} className="flex items-center gap-4">
                          <span className="w-1/3 text-sm flex items-center gap-2">{cur && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cur.color }} />}{row.village}</span>
                          <select data-testid={`village-file-select-${row.village}`} value={cur?.id || ""} disabled={saving === row.village} onChange={(e) => change(row, e.target.value || null)}
                            className="flex-1 h-10 px-3 rounded-xl border bg-background text-sm focus:ring-2 focus:ring-emerald-600 disabled:opacity-60">
                            <option value="">Select file</option>
                            {layers.map((l) => <option key={l.id} value={l.id}>{l.alias || l.name}{l.village && l.id !== cur?.id ? ` (→ ${l.village})` : ""}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t bg-muted/40 flex justify-end rounded-b-2xl"><button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-full hover:bg-muted">Close</button></div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
